import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Compass, Loader2, MapPin, Building2, CalendarDays, Star, Globe, Wine, UtensilsCrossed, ShoppingBag, Camera, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import BackButton from "@/components/BackButton";

const RADII = [10, 25, 50, 100];

const PLACE_TABS = [
  { key: "winery", label_fr: "Domaines", label_en: "Wineries", icon: Building2 },
  { key: "wine_bar", label_fr: "Bars à vin", label_en: "Wine bars", icon: Wine },
  { key: "restaurant", label_fr: "Restaurants", label_en: "Restaurants", icon: UtensilsCrossed },
  { key: "liquor_store", label_fr: "Cavistes", label_en: "Wine shops", icon: ShoppingBag },
  { key: "tourist_attraction", label_fr: "À visiter", label_en: "To visit", icon: Camera },
];

export default function Nearby() {
  const { lang } = useT();
  const [coords, setCoords] = useState(null);
  const [radius, setRadius] = useState(50);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("winery");
  const [places, setPlaces] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [producers, setProducers] = useState([]);
  const [events, setEvents] = useState([]);
  const [manualCity, setManualCity] = useState("");

  const fetchNearby = async (lat, lng, r = radius) => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        api.post("/producers/nearby", { lat, lng, radius_km: r, limit: 30 }),
        api.post("/events/nearby", { lat, lng, radius_km: r, limit: 30 }),
      ]);
      setProducers(Array.isArray(p.data) ? p.data : []);
      setEvents(Array.isArray(e.data) ? e.data : []);
    } catch (err) {
      toast.error(lang === "fr" ? "Erreur de chargement" : "Loading error");
    } finally {
      setLoading(false);
    }
    // Kick off the Google Places fetch in parallel
    fetchPlaces(lat, lng, tab, r);
  };

  const fetchPlaces = async (lat, lng, kind, r = radius) => {
    setPlacesLoading(true);
    try {
      const { data } = await api.post("/places/nearby", { lat, lng, radius_km: r, kind, limit: 20 });
      setPlaces(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setPlaces([]);
    } finally { setPlacesLoading(false); }
  };

  const onChangeTab = (k) => {
    setTab(k);
    if (coords) fetchPlaces(coords.lat, coords.lng, k, radius);
  };

  const locate = () => {
    if (!navigator.geolocation) {
      toast.error(lang === "fr" ? "Géolocalisation indisponible" : "Geolocation unavailable");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        fetchNearby(c.lat, c.lng);
      },
      () => {
        setLoading(false);
        toast.error(lang === "fr" ? "Position refusée. Saisissez une ville." : "Permission denied. Enter a city.");
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const geocodeAndSearch = async (e) => {
    e?.preventDefault();
    if (!manualCity.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/geocode", { query: manualCity.trim() });
      const c = { lat: data.lat, lng: data.lng, label: data.label };
      setCoords(c);
      await fetchNearby(c.lat, c.lng);
    } catch (err) {
      toast.error(lang === "fr" ? "Ville introuvable" : "City not found");
      setLoading(false);
    }
  };

  const onRadiusChange = (r) => {
    setRadius(r);
    if (coords) fetchNearby(coords.lat, coords.lng, r);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <BackButton fallback="/discover" className="mb-4" />
      <div className="max-w-2xl">
        <span className="chip-pink">{lang === "fr" ? "Près de vous" : "Near you"}</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
          {lang === "fr" ? "L'univers du vin autour de vous" : "Wine world around you"}
        </h1>
        <p className="text-[#4A5568] mt-3">
          {lang === "fr"
            ? "Domaines, bars à vin, restaurants, cavistes et lieux à visiter — alimenté par Google Maps et les fiches geoVINUM."
            : "Wineries, wine bars, restaurants, wine shops and places to visit — powered by Google Maps and geoVINUM listings."}
        </p>
      </div>

      <div className="mt-8 rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            data-testid="nearby-locate"
            onClick={locate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass className="w-4 h-4" />}
            {lang === "fr" ? "Utiliser ma position" : "Use my location"}
          </button>
          <form onSubmit={geocodeAndSearch} className="flex gap-2 flex-1 min-w-[260px]">
            <div className="flex-1 min-w-0">
              <AddressAutocomplete
                value={manualCity}
                onChange={setManualCity}
                onSelect={(place) => {
                  if (place?.lat != null && place?.lng != null) {
                    const c = { lat: place.lat, lng: place.lng, label: place.name || place.address };
                    setCoords(c);
                    setManualCity(place.name || place.address || manualCity);
                    fetchNearby(c.lat, c.lng);
                  }
                }}
                placeholder={lang === "fr" ? "Ville ou adresse : Beaune, Bordeaux…" : "City or address: Beaune, Bordeaux…"}
                lang={lang}
                testId="nearby-city-ac"
              />
            </div>
            <button
              data-testid="nearby-city-submit"
              type="submit"
              className="px-3 py-2 rounded-lg bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]"
            >
              {lang === "fr" ? "Chercher" : "Search"}
            </button>
          </form>
        </div>
        {coords && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#4A5568]">
            <MapPin className="w-3.5 h-3.5 text-[#E5276A]" />
            <span data-testid="nearby-coords">{coords.label || `${coords.lat.toFixed(3)}, ${coords.lng.toFixed(3)}`}</span>
            <span className="text-[#8B95A1]">·</span>
            <span className="font-bold text-[#1F3A5F]">{lang === "fr" ? "Rayon" : "Radius"} :</span>
            {RADII.map((r) => (
              <button
                key={r}
                data-testid={`nearby-radius-${r}`}
                onClick={() => onRadiusChange(r)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                  radius === r
                    ? "bg-[#E5276A] text-white border-[#E5276A]"
                    : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"
                }`}
              >
                {r} km
              </button>
            ))}
          </div>
        )}
      </div>

      {coords && (
        <div className="mt-10 space-y-10">
          {/* ─── Google Places tabs (winery / bar / restaurant / shop / visit) ─── */}
          <section data-testid="nearby-places">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-2xl text-[#1F3A5F]">
                {lang === "fr" ? "Sur Google Maps" : "On Google Maps"}
              </h2>
              {placesLoading && <Loader2 className="w-4 h-4 animate-spin text-[#E5276A]" />}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PLACE_TABS.map((tt) => {
                const Icon = tt.icon;
                return (
                  <button
                    key={tt.key}
                    data-testid={`nearby-tab-${tt.key}`}
                    onClick={() => onChangeTab(tt.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                      tab === tt.key
                        ? "bg-[#E5276A] text-white border-[#E5276A]"
                        : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {lang === "fr" ? tt.label_fr : tt.label_en}
                  </button>
                );
              })}
            </div>

            {!placesLoading && places.length === 0 && (
              <p className="mt-6 text-sm text-[#8B95A1] italic">
                {lang === "fr" ? "Aucun résultat Google Maps dans ce rayon." : "No Google Maps results in this radius."}
              </p>
            )}

            {places.length > 0 && (
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nearby-places-grid">
                {places.map((p) => (
                  <PlaceCard key={p.place_id} place={p} lang={lang} />
                ))}
              </div>
            )}
          </section>

          {/* ─── DB-stored producers + events ─── */}
          <div className="grid lg:grid-cols-2 gap-6 pt-6 border-t border-[#1F3A5F]/8">
            <section data-testid="nearby-producers">
              <h3 className="font-display text-lg text-[#1F3A5F] inline-flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#E5276A]" />
                {lang === "fr" ? "Producteurs geoVINUM" : "geoVINUM producers"} ({producers.length})
              </h3>
              {producers.length === 0 ? (
                <p className="mt-3 text-sm text-[#8B95A1] italic">
                  {lang === "fr" ? "Aucune fiche geoVINUM dans ce rayon." : "No geoVINUM listing in this radius."}
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {producers.map((p) => (
                    <li key={p.id} className="rounded-xl border border-[#1F3A5F]/12 bg-white p-4 hover:border-[#E5276A] transition-colors">
                      <Link to={`/producers/${p.slug}`} data-testid={`nearby-producer-${p.slug}`} className="block">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-[#1F3A5F]">{p.name}</div>
                            <div className="text-xs text-[#8B95A1] mt-0.5">{p.region || p.address || p.type}</div>
                          </div>
                          <span className="shrink-0 text-[11px] font-bold text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full">
                            {p.distance_km} km
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="nearby-events">
              <h3 className="font-display text-lg text-[#1F3A5F] inline-flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#E5276A]" />
                {lang === "fr" ? "Événements" : "Events"} ({events.length})
              </h3>
              {events.length === 0 ? (
                <p className="mt-3 text-sm text-[#8B95A1] italic">
                  {lang === "fr" ? "Aucun événement à proximité." : "No events nearby."}
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {events.map((e) => (
                    <li key={e.id} className="rounded-xl border border-[#1F3A5F]/12 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-[#1F3A5F]">{e.title}</div>
                          <div className="text-xs text-[#8B95A1] mt-0.5">{e.category} · {e.location}</div>
                        </div>
                        <span className="shrink-0 text-[11px] font-bold text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full">
                          {e.distance_km} km
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaceCard({ place, lang }) {
  return (
    <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-4 hover:border-[#E5276A] transition-colors" data-testid={`nearby-place-${place.place_id}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-bold text-[#1F3A5F] leading-tight">{place.name}</h4>
        {place.distance_km != null && (
          <span className="shrink-0 text-[10px] font-bold text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full">{place.distance_km} km</span>
        )}
      </div>
      {place.address && <p className="text-xs text-[#8B95A1] mt-1 line-clamp-2">{place.address}</p>}
      <div className="mt-2.5 flex items-center gap-3 text-xs">
        {place.rating != null && (
          <span className="inline-flex items-center gap-1 text-[#1F3A5F]">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <b>{place.rating}</b>
            {place.rating_count != null && <span className="text-[#8B95A1]">({place.rating_count})</span>}
          </span>
        )}
        {place.open_now != null && (
          <span className={`text-[10px] font-bold uppercase tracking-wider ${place.open_now ? "text-emerald-600" : "text-[#8B95A1]"}`}>
            {place.open_now ? (lang === "fr" ? "Ouvert" : "Open") : (lang === "fr" ? "Fermé" : "Closed")}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {place.geovinum_slug && (
          <Link to={`/producers/${place.geovinum_slug}`} data-testid={`nearby-place-claim-${place.place_id}`}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#1F3A5F] text-white">
            Fiche geoVINUM
          </Link>
        )}
        {place.gmaps_url && (
          <a href={place.gmaps_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-[#E5276A] hover:underline">
            <MapPin className="w-3 h-3" /> Maps <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {place.website && (
          <a href={place.website} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-[#1F3A5F] hover:text-[#E5276A]">
            <Globe className="w-3 h-3" /> Site
          </a>
        )}
      </div>
    </div>
  );
}
